import type { ComposerKind } from '@/mocks/composer';
import { categoryTheme } from '@/pages/marketplace/mapPlaybook';
import { GLYPH_DOT_CAP } from '../diagram';

export const MIN_CHAIN_NODES = 3;

type CardGlyphProps = {
  kind: ComposerKind;
  category: string;
  title: string;
  steps: number;
  inCount: number;
  outCount: number;
};

function spread(n: number, height: number, pad: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [height / 2];
  const span = height - pad * 2;
  return Array.from({ length: n }, (_, i) => pad + (span * i) / (n - 1));
}

function PortGlyph({ inputs, outputs }: { inputs: number; outputs: number }) {
  const left = Math.min(inputs, GLYPH_DOT_CAP);
  const right = Math.min(outputs, GLYPH_DOT_CAP);
  const leftYs = spread(left, 28, 4);
  const rightYs = spread(right, 28, 4);
  return (
    <svg viewBox="0 0 88 28" className="w-[88px] h-7 shrink-0" aria-hidden>
      <g className="text-texts" fill="currentColor" stroke="currentColor" strokeWidth="1.25">
        {leftYs.map((y, i) => (
          <g key={`in-${i}`}>
            <circle cx="7" cy={y} r="2.5" fill="currentColor" />
            <line x1="10" y1={y} x2="34" y2="14" />
          </g>
        ))}
        {rightYs.map((y, i) => (
          <g key={`out-${i}`}>
            <line x1="54" y1="14" x2="78" y2={y} />
            <circle cx="81" cy={y} r="2.5" fill="currentColor" />
          </g>
        ))}
      </g>
      <rect className="text-accent" x="34" y="7" width="20" height="14" rx="3" fill="currentColor" />
    </svg>
  );
}

function ChainGlyph({ steps }: { steps: number }) {
  const extra = Math.max(0, steps - 6);
  const shown = extra > 0 ? 5 : Math.min(Math.max(steps, 1), 6);
  const count = extra > 0 ? shown + 1 : shown;
  const width = 88;
  const gap = width / (count + 1);
  const xs = Array.from({ length: count }, (_, i) => gap * (i + 1));
  return (
    <svg viewBox="0 0 88 28" className="w-[88px] h-7 shrink-0" aria-hidden data-testid="card-glyph-chain">
      <g className="text-texts" stroke="currentColor" strokeWidth="1.25">
        {xs.slice(0, -1).map((x, i) => (
          <line key={`e-${i}`} x1={x} y1="14" x2={xs[i + 1]} y2="14" />
        ))}
      </g>
      {xs.map((x, i) => {
        const isMore = extra > 0 && i === xs.length - 1;
        if (isMore) {
          return (
            <text
              key={`n-${i}`}
              x={x}
              y="18"
              textAnchor="middle"
              className="fill-texts"
              fontSize="8"
              fontFamily="ui-monospace, monospace"
            >
              +{extra}
            </text>
          );
        }
        const isLast = i === xs.length - 1 || (extra > 0 && i === xs.length - 2);
        return (
          <circle
            key={`n-${i}`}
            cx={x}
            cy="14"
            r={isLast ? 4 : 2.5}
            className={isLast ? 'text-accent' : 'text-texts'}
            fill="currentColor"
          />
        );
      })}
    </svg>
  );
}

export function showPlaybookChain(steps: number): boolean {
  return steps >= MIN_CHAIN_NODES;
}

export default function CardGlyph({ kind, category, title, steps, inCount, outCount }: CardGlyphProps) {
  const theme = categoryTheme(category);
  const chain = kind === 'playbook' && showPlaybookChain(steps);
  const ports = kind === 'skill';
  const meta = chain ? `${steps} steps` : ports ? `${inCount} in → ${outCount} out` : null;

  return (
    <div data-testid="card-glyph" className={`w-full h-14 px-4 flex items-center gap-2.5 ${theme.wash}`}>
      <span
        className={`w-7 h-7 rounded-md bg-surface/80 border border-border/60 flex items-center justify-center shrink-0 ${theme.ink}`}
      >
        <i className={`${theme.icon} text-base`} />
      </span>
      <h3 className="flex-1 min-w-0 text-sm font-semibold text-textp group-hover:text-accent transition-colors line-clamp-1">
        {title}
      </h3>
      {chain ? <ChainGlyph steps={steps} /> : null}
      {ports ? <PortGlyph inputs={inCount} outputs={outCount} /> : null}
      {meta ? (
        <span data-testid="card-glyph-meta" className="text-[10px] font-mono text-texts shrink-0 whitespace-nowrap">
          {meta}
        </span>
      ) : null}
    </div>
  );
}
